import { DownloadOutlined } from '@ant-design/icons';
import { useGetAllMappedEventsInManagement } from '@src/api/emp/queryHooks';
import {
  useCustomDimensionManagementList,
  useGetFlowsManagementList,
  useMetricManagementList,
} from '@src/emp-3.0/api/queryHooks';
import { metricTypeMapping } from '@src/emp-3.0/components/DeploymentDrawer/constants';
import { categoryMap } from '@src/emp-3.0/constants/dimensions';
import { CUSTOM_DIMENSION, FLOW, MAPPED_EVENTS, METRICS } from '@src/emp-3.0/constants/management';
import {
  formatAttributeType,
  formatBlockEventType,
  formatStatus,
  formatTimestamp,
} from '@src/emp-3.0/utils';
import { Button, Tooltip } from 'antd';
import React from 'react';
import * as XLSX from 'xlsx';

const safeArrayJoin = (arr: any[], separator = '; ') => {
  return arr && Array.isArray(arr) ? arr.join(separator) : '';
};

const getColumnToExport = (getRelatedEventNames: (ids: any[]) => string) => ({
  [METRICS]: [
    { title: 'Metrics', dataKey: 'metric_name' },
    { title: 'Status', dataKey: 'status', executefn: (data) => formatStatus(data.status) },
    {
      title: 'Type',
      dataKey: 'metrics_type',
      executefn: (data) => metricTypeMapping[data.metrics_type] ?? data.metrics_type,
    },
    {
      title: 'Mapped Event Names',
      dataKey: 'related_event',
      executefn: (data) => getRelatedEventNames(data.related_event),
    },
    {
      title: 'Associated Dimension',
      dataKey: 'associated_dimension_names',
      executefn: (data) => safeArrayJoin(data.associated_dimension_names),
    },
    { title: 'Created By', dataKey: 'created_by' },
    { title: 'Updated By', dataKey: 'updated_by' },
    {
      title: 'Updated At',
      dataKey: 'updated_at',
      executefn: (data) => formatTimestamp(data.updated_at),
    },
    {
      title: 'Deployed At',
      dataKey: 'deployed_at',
      executefn: (data) => formatTimestamp(data.deployed_at),
    },
  ],
  [CUSTOM_DIMENSION]: [
    { title: 'Custom Dimension', dataKey: 'display_name' },
    { title: 'Status', dataKey: 'status', executefn: (data) => formatStatus(data.status) },
    {
      title: 'Tag Key Type',
      dataKey: 'attribute_type',
      executefn: (data) =>
        formatAttributeType(data.attribute_type) ??
        formatBlockEventType(data.attribute_type) ??
        data.attribute_type,
    },
    {
      title: 'Mapped Event Names',
      dataKey: 'mapped_event_names',
      executefn: (data) => safeArrayJoin(data.mapped_event_names) || 'All Applicable Mapped Events',
    },
    {
      title: 'Related Flows',
      dataKey: 'associated_flow_names',
      executefn: (data) => safeArrayJoin(data.associated_flow_names),
    },
    {
      title: 'Related Metrics',
      dataKey: 'associated_metric_names',
      executefn: (data) => safeArrayJoin(data.associated_metric_names),
    },
    {
      title: 'Mapped Tag Key',
      dataKey: 'attribute_name',
    },
    {
      title: 'Category',
      dataKey: 'category',
      executefn: (data) => {
        const category = categoryMap[data.category] ?? data.category;
        return data.is_promoted_as_global ? `${category} (Promoted)` : category;
      },
    },
    { title: 'Created By', dataKey: 'created_by' },
    { title: 'Updated By', dataKey: 'updated_by' },
    {
      title: 'Updated At',
      dataKey: 'updated_at',
      executefn: (data) => formatTimestamp(data.updated_at),
    },
    {
      title: 'Deployed At',
      dataKey: 'deployed_at',
      executefn: (data) => formatTimestamp(data.deployed_at),
    },
  ],
  [FLOW]: [
    { title: 'Flow', dataKey: 'flow_name' },
    { title: 'Status', dataKey: 'status', executefn: (data) => formatStatus(data.status) },
    {
      title: 'Mapped Event Names',
      dataKey: 'related_events',
      executefn: (data) => safeArrayJoin(data.related_events),
    },
    {
      title: 'Associated Dimension',
      dataKey: 'associated_dimension_names',
      executefn: (data) => safeArrayJoin(data.associated_dimension_names),
    },
    { title: 'Created By', dataKey: 'created_by' },
    { title: 'Updated By', dataKey: 'updated_by' },
    {
      title: 'Updated At',
      dataKey: 'updated_at',
      executefn: (data) => formatTimestamp(data.updated_at),
    },
    {
      title: 'Deployed At',
      dataKey: 'deployed_at',
      executefn: (data) => formatTimestamp(data.deployed_at),
    },
  ],
  [MAPPED_EVENTS]: [
    { title: 'Mapped Event', dataKey: 'event_name' },
    { title: 'Status', dataKey: 'status', executefn: (data) => formatStatus(data.status) },
    {
      title: 'Category',
      dataKey: 'event_category',
      executefn: (data) => categoryMap[data.event_category] ?? data.event_category,
    },
    {
      title: 'Related Metrics',
      dataKey: 'related_metrics',
      executefn: (data) => safeArrayJoin(data.related_metrics),
    },
    {
      title: 'Related Flows',
      dataKey: 'related_flows',
      executefn: (data) => safeArrayJoin(data.related_flows),
    },
    {
      title: 'Mapped From',
      dataKey: 'related_event',
      executefn: (data) => safeArrayJoin(data.related_event),
    },
    { title: 'Created By', dataKey: 'created_by' },
    { title: 'Updated By', dataKey: 'updated_by' },
    {
      title: 'Updated At',
      dataKey: 'updated_at',
      executefn: (data) => formatTimestamp(data.updated_at),
    },
    {
      title: 'Deployed At',
      dataKey: 'deployed_at',
      executefn: (data) => formatTimestamp(data.deployed_at),
    },
  ],
});

const ExportAsset = () => {
  const { data: metricList, isFetching: isLoadingMetricList } = useMetricManagementList();
  const { data: dimensionList, isFetching: isLoadingDimensionList } =
    useCustomDimensionManagementList();
  const { data: flowList, isFetching: isLoadingFlowList } = useGetFlowsManagementList();
  const { data: mappedEventsList, isFetching: isLoadingMappedEventsList } =
    useGetAllMappedEventsInManagement();

  const isLoading =
    isLoadingMetricList || isLoadingDimensionList || isLoadingFlowList || isLoadingMappedEventsList;

  const getEventNameById = (eventId: number) => {
    if (!mappedEventsList || !Array.isArray(mappedEventsList)) return '';

    for (const eventData of mappedEventsList) {
      if (eventData.event_id === eventId) {
        return eventData.versions[0]?.event_name || '';
      }
    }
    return eventId;
  };

  const getRelatedEventNames = (relatedEventIds: any[]): string => {
    if (!relatedEventIds || !Array.isArray(relatedEventIds)) return '';

    const eventNames = relatedEventIds.map((id) => {
      if (typeof id === 'number') {
        return getEventNameById(id);
      }
      return id;
    });

    return eventNames.filter((name) => name).join('; ');
  };

  const createSheetData = (data: any[], assetType: string): string[][] | null => {
    if (!data || !data.length) return null;

    const columnToExport = getColumnToExport(getRelatedEventNames);
    const columns = columnToExport[assetType] || [];
    const sheetData: string[][] = [];

    // Add headers
    const headers = columns.map((col) => col.title);
    sheetData.push(headers);

    // Add data rows
    data.forEach((rowData) => {
      // Extract the actual data from the first version if it exists
      const actualData =
        rowData.versions && rowData.versions.length > 0 ? rowData.versions[0] : rowData;

      const row = columns.map((col) => {
        try {
          if (col.executefn) {
            return col.executefn(actualData) ?? '';
          }
          return actualData[col.dataKey] ?? '';
        } catch (error) {
          return '';
        }
      });
      sheetData.push(row);
    });

    return sheetData;
  };

  const onDownloadMultiSheet = () => {
    if (isLoading) return;

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    const assetData = [
      { key: METRICS, name: 'Metrics', data: metricList },
      { key: FLOW, name: 'Flows', data: flowList },
      { key: CUSTOM_DIMENSION, name: 'Custom Dimensions', data: dimensionList },
      { key: MAPPED_EVENTS, name: 'Mapped Events', data: mappedEventsList },
    ];

    let hasData = false;

    assetData.forEach(({ key, name, data }) => {
      const sheetData = createSheetData(data, key);
      if (!sheetData) return;

      hasData = true;

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      // Auto-size columns
      const headers = sheetData[0];
      const colWidths = headers.map((header, i) => {
        const maxLength = Math.max(
          header.length,
          ...sheetData.slice(1).map((row) => String(row[i] || '').length),
        );
        return { wch: Math.min(maxLength + 2, 50) }; // Max width 50
      });
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, name);
    });

    if (!hasData) {
      return;
    }

    // Generate and download Excel file
    const fileName = `all_assets_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };
  return (
    <Tooltip title="Download All Assets">
      <Button
        onClick={onDownloadMultiSheet}
        disabled={isLoading || (!metricList && !dimensionList && !flowList && !mappedEventsList)}
        icon={<DownloadOutlined />}
        loading={isLoading}
        style={{
          fontSize: '16px',
          width: '30px',
          height: '28px',
          marginTop: '3px',
        }}
      />
    </Tooltip>
  );
};

export default ExportAsset;
